import { PagesId } from '@ts-enums/PagesId'
import { ContentType } from '@ts-types/ArticleSection'
import { type Translations } from '@ts-types/Translations'
import { getUrlByPageId } from '@utils/getUrlByPageId'
import { Languages } from '@wicadu/langscope'

const globalTranslations: Translations = {
  es: {
    seo_title: 'Cancelar pedidos',
    seo_description: 'Aprende cómo cancelar pedidos en wicadu paso a paso. Guía para propietarios y usuarios con permisos. Restaura el inventario y gestiona tus pedidos con precisión.',
    seo_keywords: 'cancelar pedidos, wicadu, gestión de inventario, cancelar pedido wicadu, guía cancelar pedido, ventas, compras, cancelar pedido paso a paso, tutorial cancelar pedido, cómo cancelar pedidos',
    page_id: PagesId.HowToCancelOrders,
    breadcrumbs: {
      home: 'Inicio',
      orders_home: 'Pedidos',
      how_to_cancel: 'Cancelar pedidos',
    },
    article: {
      title: 'Cancelar pedidos',
      intended: [
        `<strong>Propietarios</strong> o <strong>Administradores</strong> con acceso completo para gestionar pedidos y <em>configurar de la tienda</em> dentro del <a href=${process.env.STORES_WEBAPP_HOST}>asistente de wicadu</a>.`,
        `<strong>Usuarios</strong> con permisos para administrar <em>ventas</em> o <em>compras</em> y <em>cancelar pedidos</em>.`,
      ],
      about: {
        title: 'Acerca de cancelar pedidos',
        content_type: ContentType.Paragraph,
        content: [
          `La <strong>cancelación de pedidos</strong> en wicadu te permite corregir errores o responder a cambios en solicitudes de clientes o proveedores, asegurando que tu <strong>inventario</strong> permanezca preciso. Esta funcionalidad es esencial para gestionar <strong>ventas</strong> o <strong>compras</strong> realizadas, restaurando los <strong>productos</strong> al inventario tras la cancelación. Este proceso simplifica la <strong>gestión del inventario</strong> y mantiene la integridad de tus registros financieros.`,
        ],
      },
      before_begin: {
        title: 'Antes de empezar',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              `Antes de cancelar un pedido, verifica lo siguiente: `,
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  `El pedido debe estar registrado en el módulo de <em>Ventas</em> o <em>Compras</em> en <a href=${process.env.STORES_WEBAPP_HOST}>wicadu</a> y en estado Preparación.`,
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Asegúrate de tener permisos para <strong>cancelar pedidos</strong>. Consulta con tu administrador si no estás seguro.`,
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Revisa los detalles del pedido para confirmar que es el correcto, ya que la cancelación <strong>no puede deshacerse</strong>.`,
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Si el pedido está Entregado, considera generar un reembolso. Consulta:
                  <a href=${getUrlByPageId(PagesId.OrdersReturns, Languages.Spanish)}>Cómo generar un reembolso</a>.`,
                ],
              },
            ],
          },
        ],
      },
      how_to: {
        title: 'Pasos para cancelar un pedido',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Acceder al módulo correspondiente: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Dirígete a la funcionalidad de <em>Ventas</em> o <em>Compras</em> en el <strong>asistente de wicadu</strong>, según el tipo de pedido que desees cancelar.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Seleccionar el pedido: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Haz clic en el pedido en estado Preparación para acceder a sus detalles.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Iniciar la cancelación: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'En la sección <em>General</em>, selecciona el botón <strong>Cancelar</strong>.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Nota:</strong> Este botón solo estará disponible si el pedido está en Preparación.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Confirmar la cancelación: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Se mostrará un mensaje de seguridad. Escribe <strong>CANCELAR</strong> en el campo proporcionado para habilitar el botón de confirmación. Luego, haz clic en <strong>Sí, deseo cancelarlo</strong>.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Nota:</strong> Una vez confirmado, el estado del pedido cambiará automáticamente y los <strong>productos</strong> serán restaurados al <strong>inventario</strong>. Esta acción no puede revertirse directamente.',
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Siguientes pasos',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Tras <strong>cancelar el pedido</strong>, considera estas acciones: ',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Verifica el inventario: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      // `Asegúrate de que el <strong>stock</strong> de los <strong>productos</strong> se haya actualizado correctamente. Más detalles en: <a href=${getUrlByPageId(
                      //   PagesId.Home,
                      //   Languages.Spanish
                      // )}>Cómo verificar el inventario</a>`,
                      `Asegúrate de que el <strong>stock</strong> de los <strong>productos</strong> se haya actualizado correctamente.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Notificar al cliente o proveedor: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Informa a las partes involucradas sobre la cancelación para mantener una comunicación clara.',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      "<strong>Nota:</strong> Si el pedido fue realizado a través de la tienda en línea (<strong>creada con wicadu</strong>), el cliente será notificado automáticamente.",
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Registrar un nuevo pedido: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Si necesitas corregir el pedido cancelado, crea uno nuevo. Descubre cómo:
                      <a href=${getUrlByPageId(PagesId.CreateManualUserOrders, Languages.Spanish)}>Cómo crear un pedido manual</a> o
                      <a href=${getUrlByPageId(PagesId.CreateManualStoreOrders, Languages.Spanish)}>Cómo crear compras manuales</a>
                      `
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problemas Comunes',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>No veo el botón Cancelar:</strong> Asegúrate de que el pedido está en estado <em>Preparación</em>. Si está Entregado, <a href=${getUrlByPageId(PagesId.OrdersReturns, Languages.Spanish)}>genera un reembolso</a> en lugar de cancelarlo.`
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>El inventario no se actualiza:</strong> Verifica que la cancelación se completó y revisa el <strong>stock</strong> en el módulo de inventario.'
            ],
          },
        ],
      },
    }
  },
  en: {
    seo_title: 'Canceling orders',
    seo_description: 'Step-by-step guide to canceling orders in wicadu. Designed for store owners and users with permissions. Easily restore inventory and take full control of your order management.',
    seo_keywords: 'cancel orders, how to cancel an order, order cancellation process wicadu, guide to canceling orders, steps to cancel an order in wicadu, issues canceling orders wicadu, wicadu support for canceling orders, wicadu cancel order manually',
    page_id: PagesId.HowToCancelOrders,
    breadcrumbs: {
      home: 'Home',
      orders_home: 'Orders',
      how_to_cancel: 'Canceling orders',
    },
    article: {
      title: 'Canceling orders',
      intended: [
        `<strong>Store Owners</strong> or <strong>Admins</strong> with full access to manage orders and configure store settings within the <a href=${process.env.STORES_WEBAPP_HOST}>wicadu assistant</a>.`,
        `<strong>Users</strong> with permissions to handle <em>sales</em> or <em>purchases</em> and <em>cancel orders</em>.`

      ],
      about: {
        title: 'About canceling orders',
        content_type: ContentType.Paragraph,
        content: [
          `<strong>Canceling orders</strong> in <strong>wicadu</strong> allows you to correct errors or adapt to changes in customer or supplier requests, keeping your inventory accurate. This feature is important for managing <strong>sales</strong> or <strong>purchases</strong>, automatically restoring <strong>products</strong> to <strong>stock</strong> upon cancellation. This process simplifies <strong>stock management</strong> and ensures the integrity of your financial records.`
        ],
      },
      before_begin: {
        title: 'Before you begin',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              `Before canceling an order, confirm the following: `,
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  `The order must be registered in the <em>Sales</em> or <em>Purchases</em> module in <a href=${process.env.STORES_WEBAPP_HOST}>wicadu</a> and in the <em>Prepared</em> state.`
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Ensure you have permissions to <strong>cancel orders</strong>. Check with your administrator if unsure.`,
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  "Review the order details to verify it is the correct one, as <strong>cancellation cannot be undone</strong>."
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `If the order is Delivered, consider issuing a refund. See:
                  <a href=${getUrlByPageId(PagesId.OrdersReturns, Languages.English)} title='How to issue a refund'>How to issue a refund</a>.`
                ],
              },
            ],
          },
        ],
      },
      how_to: {
        title: 'Steps to cancel an order',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Access the relevant module: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Go to the <em>Sales</em> or <em>Purchases</em> section in the <strong>wicadu assistant</strong>, depending on the type of order you wish to cancel.  '
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Select the order: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'From the <b>In progress</b> section, click the order in the <em>Prepared</em> state to view its details.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Initiate cancellation: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'In the <em>General</em> section, select the <strong>Cancel</strong> button.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Note:</strong> This button is only available if the order is in the <em>Prepared</em> state.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Confirm cancellation: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'A security prompt will appear. Type <strong>CANCEL</strong> in the provided field to enable the confirmation button. Then, click <strong>Yes, I want to cancel it</strong>.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Note:</strong> Upon confirmation, the order status will update automatically, and the <strong>products</strong> will be restored to the <strong>inventory</strong>. This action cannot be reversed directly.',
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Next steps',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'After <strong>canceling the order</strong>, consider these actions:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Verify inventory: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      // `Ensure the <strong>stock</strong> of the <strong>products</strong> has updated correctly.
                      // Learn more: <a href=${getUrlByPageId(PagesId.Home, Languages.English)} title='How to verify inventory'>How to verify inventory</a>`,
                      `Ensure the <strong>stock</strong> of the <strong>products</strong> has updated correctly.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Notify the customer or supplier: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Inform relevant parties about the cancellation to maintain clear communication.'
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      "<strong>Note:</strong> If the order was placed through the store's website (<strong>powered by wicadu</strong>), the customer will be notified automatically.",
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Add a new order: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `If you need to correct the canceled order, create a new one. Discover how:
                      <a href=${getUrlByPageId(PagesId.CreateManualUserOrders, Languages.English)} title='How to add a Manual Sale'>How to add a Manual Sale</a> o
                      <a href=${getUrlByPageId(PagesId.CreateManualStoreOrders, Languages.English)} title='How to add a Manual Purchase'>How to add a Manual Purchase</a>
                      `
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Common issues',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>Can't see the Cancel button:</strong> Ensure the order is in the <em>Prepared</em> state. If it's Delivered, <a href=${getUrlByPageId(PagesId.OrdersReturns, Languages.English)} title='How to issue a refund'>How to issue a refund</a> instead of canceling.`
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Inventory does not update:</strong> Confirm the cancellation was completed and check the <strong>stock</strong> in the inventory module.'
            ],
          },
        ],
      },
    }
  },
  fr: {
    seo_title: 'Annuler des commandes',
    seo_description:
      'Découvrez comment annuler correctement une commande dans wicadu et dans quels cas utiliser un remboursement à la place.',
    seo_keywords:
      'annuler commande, statut commande, remboursement, commande préparée, commande livrée, wicadu commandes',
    page_id: PagesId.HowToCancelOrders,
    breadcrumbs: {
      home: 'Accueil',
      orders_home: 'Commandes',
      how_to_cancel: 'Annuler des commandes',
    },
    article: {
      title: 'Annuler des commandes',
      intended: [
        `<strong>Propriétaires</strong>, <strong>administrateurs</strong> et équipes qui gèrent les <em>commandes</em> dans l’<a href=${process.env.STORES_WEBAPP_HOST}>assistant wicadu</a>.`,
        '<strong>Vendeurs</strong> autorisés à modifier l’état des commandes et à corriger des erreurs opérationnelles.',
      ],
      about: {
        title: 'À propos de l’annulation des commandes',
        content_type: ContentType.Paragraph,
        content: [
          'L’<strong>annulation de commande</strong> dans <strong>wicadu</strong> vous permet de corriger une erreur, de répondre à un changement de demande d’un client ou d’un fournisseur, et de garder votre <strong>inventaire</strong> exact. Cette fonctionnalité est importante pour la gestion des <strong>ventes</strong> et des <strong>achats</strong>, car elle rétablit les <strong>produits</strong> dans le <strong>stock</strong> lorsque l’annulation est confirmée.',
          'En utilisant correctement ce processus, vous simplifiez la <strong>gestion des commandes</strong>, améliorez le suivi du <strong>stock</strong> et protégez la cohérence de vos enregistrements financiers. C’est également une bonne pratique pour distinguer les commandes abandonnées des commandes réellement <strong>livrées</strong>, <strong>retournées</strong> ou <strong>remboursées</strong>.',
        ],
      },
      before_begin: {
        title: 'Avant de commencer',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Avant d’annuler une commande, confirmez les points suivants :',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  `La commande doit être enregistrée dans le module <em>Ventes</em> ou <em>Achats</em> de <a href=${process.env.STORES_WEBAPP_HOST}>wicadu</a> et être dans l’état <em>Préparée</em>.`,
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Assurez-vous de disposer des autorisations nécessaires pour <strong>annuler des commandes</strong>. En cas de doute, vérifiez avec votre administrateur.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Contrôlez soigneusement les détails de la commande afin de confirmer qu’il s’agit bien de la bonne, car cette <strong>annulation ne peut pas être annulée directement</strong>.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Si la commande est déjà <em>Livrée</em>, envisagez plutôt un remboursement. Consultez :
                  <a href=${getUrlByPageId(PagesId.OrdersReturns, Languages.French)} title="Comment traiter un retour, un échange ou un remboursement dans wicadu">Comment traiter un retour, un échange ou un remboursement</a>.`,
                ],
              },
            ],
          },
        ],
      },
      how_to: {
        title: 'Comment annuler une commande',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Accéder au module concerné : '] },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Rendez-vous dans la section <em>Ventes</em> ou <em>Achats</em> de l’<strong>assistant wicadu</strong>, selon le type de commande que vous souhaitez annuler.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Sélectionner la commande : '] },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Depuis la section des commandes en cours, cliquez sur la commande en état <em>Préparée</em> pour afficher ses détails.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Lancer l’annulation : '] },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Dans la section <em>Général</em>, sélectionnez le bouton <strong>Annuler</strong>.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Remarque :</strong> ce bouton n’est disponible que si la commande est dans l’état <em>Préparée</em>.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Confirmer l’annulation : '] },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Une fenêtre de sécurité s’affichera. Saisissez <strong>ANNULER</strong> dans le champ prévu afin d’activer le bouton de confirmation, puis cliquez sur <strong>Oui, je souhaite annuler</strong>.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Remarque :</strong> une fois l’action confirmée, le statut de la commande sera mis à jour automatiquement et les <strong>produits</strong> seront réintégrés dans l’<strong>inventaire</strong>. Cette action ne peut pas être restaurée directement.',
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Étapes suivantes',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: ['Après <strong>l’annulation de la commande</strong>, pensez aux actions suivantes :'],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Vérifier l’inventaire : '] },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Assurez-vous que le <strong>stock</strong> des <strong>produits</strong> a bien été mis à jour après l’annulation.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Informer le client ou le fournisseur : '] },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Prévenez les parties concernées afin de conserver une communication claire autour de l’annulation.',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      "<strong>Remarque :</strong> si la commande a été passée via la boutique en ligne propulsée par <strong>wicadu</strong>, le client sera notifié automatiquement.",
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Créer une nouvelle commande : '] },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Si vous devez corriger la commande annulée, créez-en une nouvelle. Consultez :
                      <a href=${getUrlByPageId(PagesId.CreateManualUserOrders, Languages.French)} title="Comment ajouter une vente manuelle dans wicadu">Comment ajouter une vente manuelle</a> ou
                      <a href=${getUrlByPageId(PagesId.CreateManualStoreOrders, Languages.French)} title="Comment créer un achat manuel dans wicadu">Comment ajouter un achat manuel</a>.`,
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problèmes fréquents',
        content_type: ContentType.UnorderedList,
        content: [
          { content_type: ContentType.Paragraph, content: ['<strong>Le bouton Annuler n’apparaît pas</strong> : vérifiez si l’état actuel autorise encore l’annulation.'] },
          { content_type: ContentType.Paragraph, content: ['<strong>La commande a déjà été livrée</strong> : utilisez plutôt un flux de retour ou de remboursement.'] },
        ],
      },
    },
  },
  pt: {},
}


export default globalTranslations
